/**
 * Defines keywords used in schema definitions to control UI behavior and presentation.
 * These keywords extend standard JSON Schema with UI-specific metadata.
 */
export enum SchemaKeywords {
  UI = 'ui',
  UiIcon = '_ui_icon',

  /**
   * Defines the specific widget type to render for a schema property.
   * Used to override the default input component (based on `type`) for a JSON schema property.
   * @see {@link SfoUiWidgetType} For available widget options
   * @example
   * '_ui_widget': 'sfo-rich-text'
   */
  UiWidget = '_ui_widget',

  /**
   * Build-time-only tag placed on an ARRAY property to set the widget for each
   * of its items. The schema builder ({@link build-schema-into-json}) relocates
   * `_ui_items_widget` onto `items._ui_widget` and deletes the source key, so the
   * form builder only ever sees `items._ui_widget` at runtime (it does not read
   * `_ui_items_widget` directly). Needed because typescript-json-schema drops a
   * primitive type alias's `@_ui_widget` when inlining the alias into array `items`.
   * @example
   * // on an array property: revisedDates?: string[]
   * '_ui_items_widget': 'sfo-date-input'
   */
  UiItemsWidget = '_ui_items_widget',

  /**
   * Tags a schema property for grouping or categorization.
   * Used by filtering and organization features.
   * @example
   * '_ui_tag': 'advanced'
   */
  UiTag = '_ui_tag',

  /**
   * Provides instructional content or contextual help text about a schema section.
   *
   * When present, this content is displayed in an {@link SfoAdmonitionComponent}
   * to help users understand the purpose of form fields or sections.
   *
   * Note: Currently only rendered in {@link SfoDrawerViewComponent} layouts.
   *
   * @example
   * // Basic usage
   * '_readme': 'This section controls page formatting options.'
   *
   * @example
   * // With markdown formatting
   * '_readme': '## Layout Settings\n\nThese options affect how content is arranged on the page.'
   *
   * @see {@link SfoAdmonitionComponent} For the component that renders this content
   * @see {@link SfoDrawerViewComponent} For the layout component that supports readme displays
   */
  Readme = '_readme',

  /**
   * Groups related form fields together for visual organization.
   * Fields with matching group values appear together in the UI.
   * (Only supported in Tab View)
   * @example
   * '_ui_group': 'Layout'
   */
  UiGroup = '_ui_group',

  /**
   * When true, hides the field from the rendered form.
   * Field remains in the form model but is not visible to users.
   * @example
   * '_ui_hide': true
   */
  UiHide = '_ui_hide',

  /**
   * When true, disables user interaction with the field.
   * Field value is visible but cannot be modified.
   * @example
   * '_ui_disabled': true
   */
  UiDisabled = '_ui_disable',

  /**
   * Indicates whether the field can return null values.
   * @example
   * '_nullable': true
   */
  Nullable = '_nullable',

  /** Specifies a component to use for rendering */
  Component = 'component',

  /**
   * Allows multiple values to be selected for a dropdown.
   */
  Multiple = '_multiple',

  /**
   * Specifies the width of the table column using basscss classes.
   */
  UiTableWidth = '_ui_table_width',

  /**
   * Specifies the class to apply to a UI element.
   */
  UiClass = '_ui_class',

  /**
   * Marks a field as secondary: when it is EMPTY at form load it is collapsed
   * behind its section's "More fields" menu instead of rendering an empty
   * input. A filled or revealed field always renders. Only the grid layout
   * reads this keyword; flat renderings ignore it.
   * @example
   * '_ui_secondary': true
   */
  UiSecondary = '_ui_secondary',

  /**
   * Marks a schema field as deprecated. Emitted by the schema generator
   * (`json-schema-2.ts`'s `validAtKeywords`) from `@deprecated` JSDoc tags
   * on `*.schema.ts` interface fields.
   *
   * NOTE for future enum additions: `deprecated`, `readOnly`, `writeOnly`,
   * and `examples` are JSON Schema 2019-09 standard annotation keywords
   * that Ajv 8 ships with built-in. Calling `ajv.addKeyword(name)` for
   * any of those throws `Keyword <name> is already defined`. The form-
   * builder and the tdk validator both guard against this with an
   * `AJV_BUILTIN_ANNOTATIONS` allow-list — extend that list there too if
   * you add another standard-annotation entry to this enum.
   *
   * Value is the deprecation reason as a STRING (the JSDoc text after the
   * tag) — typescript-json-schema 0.65.1 emits `@deprecated <reason>` as
   * `"deprecated": "<reason>"` rather than the JSON Schema 2019-09
   * boolean form `"deprecated": true`. This is a deliberate divergence:
   * the reason text is more useful for form UI (badge tooltip, hover
   * help) than a bare boolean, and no current consumer enforces a
   * meta-schema that would reject the string value. Form-UI consumers
   * should treat the mere presence of the key as the deprecation
   * signal — `if (schema.deprecated) { …badge… }` — rather than
   * comparing for `=== true`.
   *
   * Bare `@deprecated` (no reason) emits `"deprecated": ""` (empty
   * string), which is FALSY by JavaScript truthy-check. If you need
   * the field to badge, always supply a reason in the JSDoc tag.
   *
   * @example
   * '@deprecated Use transTitles array instead'  // → "deprecated": "Use transTitles array instead"
   */
  Deprecated = 'deprecated',
}

/**
 * Defines the available specialized input widgets that can be assigned
 * to form fields through the UiWidget schema keyword.
 */
export enum SfoUiWidgetType {
  /** Date input field with date picker */
  DateInput = 'sfo-date-input',
  /** CSL date-variable input rendered as simple text; stores [string] */
  DateVariableInput = 'sfo-date-variable-input',

  /** Code editor with syntax highlighting */
  CodeEditor = 'sfo-code-editor',

  /** Rich text editor with formatting options */
  RichText = 'sfo-rich-text',

  /** Multi-line text area input */
  Textarea = 'sfo-textarea',

  /** Radio button group for selecting from predefined options */
  RadioGroup = 'sfo-radio-group',

  /** Dropdown selection menu */
  EnumInput = 'sfo-enum-input',

  /** Creator/author role array with person or literal modes */
  CreatorRoleArray = 'sfo-creator-role-array',

  /** Font size input (numeric + unit/keyword) */
  FontSizeCss = 'sfo-font-size-css',
}

// TODO: these keywords are also listed by hand in validAtKeywords
// (libs/export/src/html/json-schema-2.ts); derive one list from the other so they cannot drift.
